/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'
import relativeImg from 'assets/images/relative.png'
import position_fixedGif from 'assets/images/position_fixed.gif'
import position_stickyGif from 'assets/images/position_sticky.gif'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Позиционирование элементов</h2>
    <p className={styles.description}>
      Браузер отображает на странице содержимое документа html, следуя потоку выполнения документа. Для европейских
      языков поток выполнения такой же, как правила чтения - сверху вниз, слева направо.{' '}
    </p>
    <p className={styles.description}>
      А как будет, если элемент расположен над границей двух блоков? Или как заставить меню оставаться всегда на одном
      месте страницы, в то время как остальное содержимое можно перемещать с помощью скролла? Именно для решения
      подобных задач было создано свойство <code className={styles.code}>position</code>.{' '}
    </p>
    <p className={styles.description}>
      Это свойство может словно приподнять элемент над потоком выполнения и разместить элемент над любым элементом,
      находящимся в потоке. А что будет если два таких элемента вне потока окажутся в одном месте, кто будет в тени, а
      кто на вершине? Эту проблему решает другое свойство - <code className={styles.code}>z-index</code>.{' '}
    </p>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - position</h3>
      <p className={styles.description}>
        Задает особые правила размещения элемента. В частности для определения положения элемента используются свойства
        <code className={styles.code}>top</code>, <code className={styles.code}>left</code>,{' '}
        <code className={styles.code}>bottom</code> или <code className={styles.code}>right</code>, которые неприменимы
        к элементам без <code className={styles.code}>position</code>.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h3}>1.1.1 - Относительное позиционирование</h4>
        <p className={styles.description}>
          При <code className={styles.code}>position: relative</code>, элемент остается в потоке: его размеры и
          положение рассчитываются для всех соседних блоков согласно <code className={styles.code}>box-model</code>.
          Прорисовка изображения производится браузером с учетом заданный свойств{' '}
          <code className={styles.code}>top</code>, <code className={styles.code}>left</code>,{' '}
          <code className={styles.code}>bottom</code> или <code className={styles.code}>right</code>. При этом может
          происходить перекрытие изображения соседних элементов.{' '}
        </p>
        <p className={styles.description}>
          Смещение элемента <code className={styles.code}>R</code> с{' '}
          <code className={styles.code}>position: relative</code>{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="relative_shift"
          src="https://codepen.io/GoIT-fe/embed/GvjLJM?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.description}>
          Запомнить, как применять свойства <code className={styles.code}>top</code>,{' '}
          <code className={styles.code}>left</code>, <code className={styles.code}>bottom</code> или{' '}
          <code className={styles.code}>right</code> для <code className={styles.code}>position: relative</code>{' '}
          довольно легко.{' '}
        </p>
        <img className={styles.img} src={relativeImg} alt="relative" />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Абсолютное позиционирование</h3>
      <p className={styles.description}>
        <code className={styles.code}>position: absolute</code> - поднимает элемент над потоком, т.е. после применения
        этого свойства соседние элементы исключат этот элемент из расчета для определения своего положения.{' '}
      </p>
      <p className={styles.description}>
        Отсчет своих top, left, bottom или right для absolute-элемента ведется относительно ближайшего предка со
        свойством, отличным от свойства <code className={styles.code}>position: static</code>. Если такового предка не
        окажется, то отсчет будет произведен от <code className={styles.code}>body</code>.{' '}
      </p>
      <p className={styles.notificationWarn}>
        Когда к элементу добавляется свойство <code className={styles.code}>position: absolute</code>, то всегда следует
        ответить на вопрос: "Какой элемент будет являться тем предком, к которому потребуется применить свойство{' '}
        <code className={styles.code}>position: relative</code> ?". И после ответа на этот вопрос необходимо убедиться,
        что <code className={styles.code}>position: relative</code> установлено.{' '}
      </p>
      <p className={styles.description}>
        Если при расчете положения элемента в потоке нужно было учитывать только верхний левый угол элемента и
        родительского контейнера, то отсчет положения абсолютно позиционированного элемента другой.{' '}
      </p>
      <p className={styles.description}>
        Запись <code className={styles.code}>bottom: 20px</code> означает расстояние между нижней границей родительского
        контейнера и нижней границей самого элемента. Аналогично работают и <code className={styles.code}>right</code>,{' '}
        <code className={styles.code}>left</code> и <code className={styles.code}>top</code> - задается расстояние между
        одноименными сторонами контейнера и элемента.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>right: 50px</code> - правая граница элемента на 50px от правого края контейнера{' '}
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>top: 50px</code> - верхняя граница элемента на 50px от верхнего края контейнера{' '}
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>bottom: 50px</code> - нижняя граница элемента на 50px от нижнего края контейнера{' '}
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>left: 50px</code> - левая граница элемента на 50px от левого края контейнера
        </li>
      </ul>
      <p className={styles.description}>
        Ниже - скриншот, демонстрирующий применение <code className={styles.code}>position: absolute</code>. Черная
        точка указывает на начало отсчета, т.е. верхнюю левую точку первого родительского контейнера с{' '}
        <code className={styles.code}>position: relative</code>. Блок по центру отцентрирован с помощью{' '}
        <code className={styles.code}>absolute</code> и <code className={styles.code}>transform: translate</code>.
        Остальные блоки показывают смещение в зависимости от свойств и значений.{' '}
      </p>
      <p className={styles.description}>
        Свойство <code className={styles.code}>transform</code> и его значение{' '}
        <code className={styles.code}>translate(translateX, translateY)</code>
        смещают элемент от его текущего положения. <code className={styles.code}>translateX</code> смещает верхний левый
        угол элемента по горизонтали, а <code className={styles.code}>translateY</code> - по вертикали. Т.е. расчет
        положения такой же, как и в <code className={styles.code}>box-model</code>. Такое дублирование способа
        перемещения нужно потому, что с помощью свойства <code className={styles.code}>transform</code> можно
        осуществлять анимацию, о чем подробно будет рассказано позже.{' '}
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="relative_shift"
        src="https://codepen.io/GoIT-fe/embed/wqzVqY?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />

      <p className={styles.description}>
        Не стоит забывать, что на элементы с <code className={styles.code}>position</code> продолжают действовать
        правила, по которым эти элементы будут смещаться при <code className={styles.code}>margin</code> и менять размер
        при <code className={styles.code}>padding</code>. Эта особенность позволяет центрировать элемент по вертикали
        контейнера.{' '}
      </p>
      <p className={styles.description}>
        Для этого смещаем левый верхний край по центру контейнера, а затем смещаем весь элемент влево и вверх на
        половину ширины и высоты самого элемента.{' '}
      </p>
      <CopyBlock
        text={`element {
  position: absolute;
  left: 50%;
  right: 50%;
  transform: translateX(-50%) translateY(-50%);
}`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - Позиционирование с фиксацией положения</h3>
      <p className={styles.description}>
        <code className={styles.code}>position:fixed</code> это аналог значения{' '}
        <code className={styles.code}>absolute</code>. Отличие заключается в точке отсчета. Для{' '}
        <code className={styles.code}>fixed</code> отсчет всегда ведется от границ окна браузера. Это создает эффект
        фиксации элемента при использовании вертикальной прокрутки. Как правило, свойство{' '}
        <code className={styles.code}>position: fixed</code> используют для элементов навигации, позволяющих перейти к
        началу страницы, или для верхнего меню, как в примере на изображении.
      </p>
      <img className={styles.img} src={position_fixedGif} alt="position fixed" />
      <p className={styles.description}>
        для демонстрации работы <code className={styles.code}>position fixed</code> наведите курсор под надписью "I am
        fixed" и используйте колесо мыши для прокрутки контента, т.к. <code className={styles.code}>scrollbar</code>{' '}
        этого примера был искусственно спрятан.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="relative_shift"
        src="https://codepen.io/GoIT-fe/embed/OjbPVQ?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.4 - "Липкое" позиционирование</h3>
      <p className={styles.description}>
        <code className={styles.code}>position: sticky</code> это комбинация свойств{' '}
        <code className={styles.code}>relative</code> и <code className={styles.code}>fixed</code>. Пока элемент
        находится в своем контейнере и контейнер не покидает область экрана при вертикальной прокрутке это{' '}
        <code className={styles.code}>relative</code>. Если часть родительского контейнера покинула видимую часть
        экрана, то свойство меняется на <code className={styles.code}>fixed</code>, т.е. фиксируется на странице пока
        часть контейнера присутствует на экране. Если весь контейнер покинет область видимости, то опять включится
        свойство <code className={styles.code}>relative</code>, т.е. изображение элемента исчезнет с экрана вслед за
        контейнером.{' '}
      </p>
      <img className={styles.img} src={position_stickyGif} alt="position sticky" />
      <iframe
        height="300"
        scrolling="no"
        title="relative_shift"
        src="https://codepen.io/GoIT-fe/embed/YxpvYJ?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.5 - Отмена действия позиционирования</h3>
      <p className={styles.description}>
        <code className={styles.code}>position: static</code> это обычное (по умолчанию) значение свойства
        <code className={styles.code}>position</code> всех элементов. Его можно изменить, задав другое значение. А затем
        отменить действие <code className={styles.code}>relative</code>, <code className={styles.code}>absolute</code>{' '}
        или <code className={styles.code}>fixed</code> присвоив снова значение{' '}
        <code className={styles.code}>static</code>.{' '}
      </p>
    </section>
  </section>
))
